import { useState } from 'react';
import { COMPANY } from '../data/seedData';

export default function ContactForm({ variant = 'light', showCheckboxes = false }) {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    subject: '',
    message: '',
    website_url: '', // Mandatory honeypot
  });

  const [checkboxes, setCheckboxes] = useState({
    quote: false,
    urgent: false,
    consent: true,
  });

  const [files, setFiles] = useState([]);
  const [status, setStatus] = useState({ type: null, message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setCheckboxes((prev) => ({ ...prev, [name]: checked }));
  };

  const handleFileChange = (e) => {
    if (e.target.files) {
      const selectedFiles = Array.from(e.target.files);
      // Validate max 5 files, each max 10MB
      if (selectedFiles.length > 5) {
        setStatus({ type: 'error', message: 'Du kan højst vedhæfte 5 filer.' });
        return;
      }
      for (const file of selectedFiles) {
        if (file.size > 10 * 1024 * 1024) {
          setStatus({ type: 'error', message: `Filen "${file.name}" overstiger maksimumstørrelsen på 10 MB.` });
          return;
        }
      }
      setFiles(selectedFiles);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus({ type: null, message: '' });

    // Client validation
    if (!formData.name.trim() || !formData.phone.trim()) {
      setStatus({ type: 'error', message: 'Udfyld venligst både navn og telefonnummer.' });
      setIsSubmitting(false);
      return;
    }

    try {
      const interests = [];
      if (checkboxes.quote) interests.push('Uforpligtende tilbud ønskes');
      if (checkboxes.urgent) interests.push('Akut reparation nødvendig');
      const enrichedMessage = interests.length > 0 
        ? `${formData.message ? formData.message + '\n\n' : ''}[Bemærkninger: ${interests.join(', ')}]`
        : formData.message;

      let response;
      if (files.length > 0) {
        // Multipart FormData for files
        const formPayload = new FormData();
        formPayload.append('name', formData.name);
        formPayload.append('phone', formData.phone);
        formPayload.append('email', formData.email);
        formPayload.append('subject', formData.subject || 'Henvendelse fra hjemmeside');
        formPayload.append('message', enrichedMessage);
        formPayload.append('website_url', formData.website_url); // Honeypot
        files.forEach((file) => {
          formPayload.append('attachments', file);
        });

        response = await fetch('/_api/webform/submit', {
          method: 'POST',
          body: formPayload,
        });
      } else {
        // Plain JSON
        response = await fetch('/_api/webform/submit', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            name: formData.name,
            phone: formData.phone,
            email: formData.email,
            subject: formData.subject || 'Henvendelse fra hjemmeside',
            message: enrichedMessage,
            website_url: formData.website_url, // Honeypot
          }),
        });
      }

      if (response.ok) {
        setStatus({
          type: 'success',
          message: 'Tak for din henvendelse! Vi har modtaget din besked og vender tilbage hurtigst muligt.',
        });
        setFormData({
          name: '',
          phone: '',
          email: '',
          subject: '',
          message: '',
          website_url: '',
        });
        setFiles([]);
      } else {
        const errorData = await response.json().catch(() => ({}));
        setStatus({
          type: 'error',
          message: errorData.message || 'Der opstod en fejl ved afsendelse. Prøv venligst igen eller ring direkte.',
        });
      }
    } catch (err) {
      console.error('Submit error:', err);
      setStatus({
        type: 'error',
        message: 'Netværksfejl. Kontrollér din forbindelse eller kontakt os telefonisk.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const isDark = variant === 'reference' || variant === 'dark';

  return (
    <div
      className={
        isDark
          ? 'bg-slate-900/90 backdrop-blur-md rounded-2xl border border-slate-700/80 p-6 sm:p-8 lg:p-9 shadow-2xl'
          : 'bg-white rounded-xl shadow-lg border border-slate-200/80 p-6 sm:p-8'
      }
    >
      <div className="mb-6">
        <h3
          className={`text-xl sm:text-2xl font-bold tracking-tight ${
            isDark ? 'text-white' : 'text-slate-900'
          }`}
        >
          Send en forespørgsel
        </h3>
        <p className={`text-sm mt-1 ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
          Udfyld formularen herunder, så vender vi hurtigt tilbage til dig med rådgivning eller et uforpligtende tilbud.
        </p>
      </div>

      {status.type === 'success' ? (
        <div className="rounded-xl bg-emerald-950/60 border border-emerald-500/40 p-6 text-emerald-100 space-y-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#1e9457] text-white flex items-center justify-center flex-shrink-0 shadow">
              <i className="fa-solid fa-check text-lg"></i>
            </div>
            <div>
              <h4 className="font-bold text-lg text-white">Tak for din henvendelse!</h4>
              <p className="text-sm text-emerald-200">{status.message}</p>
            </div>
          </div>
          <div className="pt-2 text-xs text-emerald-300 border-t border-emerald-500/30">
            Haster det? Du kan altid ringe direkte til os på{' '}
            <a href={`tel:${COMPANY.phoneRaw}`} className="font-bold underline text-white whitespace-nowrap">
              {COMPANY.phone}
            </a>
            .
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Honeypot field (hidden from humans, visible to bots) */}
          <div className="sr-only absolute left-[-9999px]" aria-hidden="true">
            <label htmlFor="website_url">Website URL (leave blank)</label>
            <input
              type="text"
              id="website_url"
              name="website_url"
              value={formData.website_url}
              onChange={handleChange}
              tabIndex={-1}
              autoComplete="off"
            />
          </div>

          {status.type === 'error' && (
            <div className="rounded-lg bg-rose-950/80 border border-rose-500/50 p-3.5 text-rose-200 text-sm flex items-center gap-2">
              <i className="fa-solid fa-circle-exclamation text-rose-400"></i>
              <span>{status.message}</span>
            </div>
          )}

          {/* Name & Phone */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label
                htmlFor="name"
                className={`block text-xs font-semibold uppercase tracking-wider mb-1.5 ${
                  isDark ? 'text-slate-300' : 'text-slate-700'
                }`}
              >
                Navn <span className="text-[#1e9457]">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                required
                value={formData.name}
                onChange={handleChange}
                placeholder="Dit fulde navn"
                className="w-full px-3.5 py-2.5 rounded-lg bg-white text-slate-900 border border-slate-200 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#1e9457] focus:border-transparent text-sm transition-all"
              />
            </div>

            <div>
              <label
                htmlFor="phone"
                className={`block text-xs font-semibold uppercase tracking-wider mb-1.5 ${
                  isDark ? 'text-slate-300' : 'text-slate-700'
                }`}
              >
                Telefonnummer <span className="text-[#1e9457]">*</span>
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                required
                value={formData.phone}
                onChange={handleChange}
                placeholder="Fx 30 13 61 16"
                className="w-full px-3.5 py-2.5 rounded-lg bg-white text-slate-900 border border-slate-200 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#1e9457] focus:border-transparent text-sm transition-all"
              />
            </div>
          </div>

          {/* Email & Subject */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label
                htmlFor="email"
                className={`block text-xs font-semibold uppercase tracking-wider mb-1.5 ${
                  isDark ? 'text-slate-300' : 'text-slate-700'
                }`}
              >
                E-mailadresse
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="din@email.dk"
                className="w-full px-3.5 py-2.5 rounded-lg bg-white text-slate-900 border border-slate-200 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#1e9457] focus:border-transparent text-sm transition-all"
              />
            </div>

            <div>
              <label
                htmlFor="subject"
                className={`block text-xs font-semibold uppercase tracking-wider mb-1.5 ${
                  isDark ? 'text-slate-300' : 'text-slate-700'
                }`}
              >
                Emne / Henvendelsestype
              </label>
              <select
                id="subject"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                className="w-full px-3.5 py-2.5 rounded-lg bg-white text-slate-900 border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#1e9457] focus:border-transparent text-sm transition-all"
              >
                <option value="">Vælg emne...</option>
                <option value="Udkørende reparation">Udkørende glasfiberreparation</option>
                <option value="Pumpebrønde & tanke">Pumpebrønde & tanke</option>
                <option value="Indstøbningsbrønde">Indstøbningsbrønde</option>
                <option value="Specialopgaver">Specialopgaver / Formbygning</option>
                <option value="Standard produkter">Standard produkter (containere/igloer)</option>
                <option value="Andet">Andet</option>
              </select>
            </div>
          </div>

          {/* Message */}
          <div>
            <label
              htmlFor="message"
              className={`block text-xs font-semibold uppercase tracking-wider mb-1.5 ${
                isDark ? 'text-slate-300' : 'text-slate-700'
              }`}
            >
              Beskrivelse af opgaven
            </label>
            <textarea
              id="message"
              name="message"
              rows={3}
              value={formData.message}
              onChange={handleChange}
              placeholder="Beskriv gerne emnet, mål, placering eller hvad opgaven drejer sig om..."
              className="w-full px-3.5 py-2.5 rounded-lg bg-white text-slate-900 border border-slate-200 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#1e9457] focus:border-transparent text-sm transition-all resize-y"
            ></textarea>
          </div>

          {/* Checkboxes (Design Reference Requirement) */}
          {(showCheckboxes || isDark) && (
            <div className="space-y-2.5 pt-1">
              <label className="flex items-center gap-2.5 cursor-pointer text-xs sm:text-sm text-slate-300 select-none">
                <input
                  type="checkbox"
                  name="quote"
                  checked={checkboxes.quote}
                  onChange={handleCheckboxChange}
                  className="w-4 h-4 rounded text-[#1e9457] focus:ring-[#1e9457] border-slate-400 bg-white cursor-pointer accent-[#1e9457]"
                />
                <span>Jeg ønsker et uforpligtende tilbud eller prisoverslag</span>
              </label>

              <label className="flex items-center gap-2.5 cursor-pointer text-xs sm:text-sm text-slate-300 select-none">
                <input
                  type="checkbox"
                  name="urgent"
                  checked={checkboxes.urgent}
                  onChange={handleCheckboxChange}
                  className="w-4 h-4 rounded text-[#1e9457] focus:ring-[#1e9457] border-slate-400 bg-white cursor-pointer accent-[#1e9457]"
                />
                <span>Haster / Akut udkørende reparation nødvendig</span>
              </label>

              <label className="flex items-center gap-2.5 cursor-pointer text-xs sm:text-sm text-slate-300 select-none">
                <input
                  type="checkbox"
                  name="consent"
                  checked={checkboxes.consent}
                  onChange={handleCheckboxChange}
                  className="w-4 h-4 rounded text-[#1e9457] focus:ring-[#1e9457] border-slate-400 bg-white cursor-pointer accent-[#1e9457]"
                />
                <span className="text-[11px] sm:text-xs text-slate-400">
                  Jeg accepterer at blive kontaktet af RB Glasfiber vedrørende min forespørgsel
                </span>
              </label>
            </div>
          )}

          {/* File Upload Button (Transparent background with accent color border) */}
          <div>
            <label
              htmlFor="attachments"
              className={`block text-xs font-semibold uppercase tracking-wider mb-1.5 ${
                isDark ? 'text-slate-300' : 'text-slate-700'
              }`}
            >
              Vedhæft filer (valgfrit)
            </label>
            <label
              htmlFor="attachments"
              className="w-full flex flex-col items-center justify-center p-3.5 rounded-lg bg-transparent border-2 border-dashed border-[#1e9457] hover:border-[#22c55e] text-slate-200 hover:text-white hover:bg-[#1e9457]/10 transition-all cursor-pointer group"
            >
              <div className="flex items-center gap-2 text-xs sm:text-sm font-bold text-[#1e9457] group-hover:text-[#22c55e]">
                <i className="fa-solid fa-cloud-arrow-up text-base"></i>
                <span>Vælg filer eller træk hertil (tegninger / fotos)</span>
              </div>
              <span className="text-[11px] text-slate-400 mt-1">PDF, PNG, JPG eller WEBP (maks. 10 MB pr. fil)</span>
              <input
                id="attachments"
                name="attachments"
                type="file"
                multiple
                accept=".pdf,.png,.jpg,.jpeg,.webp"
                onChange={handleFileChange}
                className="sr-only"
              />
            </label>
            {files.length > 0 && (
              <div className="mt-2 text-xs font-semibold text-[#22c55e] flex items-center gap-1.5">
                <i className="fa-solid fa-circle-check"></i>
                <span>{files.length} fil(er) valgt: {files.map((f) => f.name).join(', ')}</span>
              </div>
            )}
          </div>

          {/* Submit CTA (Accent color background) */}
          <div className="pt-2">
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full py-3.5 px-6 rounded-lg bg-[#1e9457] hover:bg-[#15703f] text-white font-bold uppercase tracking-wider text-sm shadow-md hover:shadow-lg transition-all duration-200 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <i className="fa-solid fa-circle-notch fa-spin"></i>
                  <span>Sender besked...</span>
                </>
              ) : (
                <>
                  <span>Send henvendelse</span>
                  <i className="fa-solid fa-paper-plane text-xs"></i>
                </>
              )}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
